import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios'; // Import axios
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { supabase } from './config/supabase';
import './App.css';

// Import components
import Login from './components/Login';
import CreateTicket from './components/CreateTicket';
import TrackTickets from './components/TrackTickets';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import DevOpsDashboard from './components/DevOpsDashboard';
import SupportDashboard from './components/SupportDashboard';
import MonitoringDashboard from './components/MonitoringDashboard';
import AwsAccounts from './components/AwsAccounts';
import AwsCostDashboard from './components/AwsCostDashboard';
import GitHubDashboard from './components/GitHubDashboard';
import Users from './components/Users';
import Repositories from './components/Repositories';
import Issues from './components/Issues';
import PullRequests from './components/PullRequests';
import DailyProductivity from "./components/DailyProductivity";
import Profile from './components/Profile';
import CloudDashboard from './components/CloudDashboard';
import SecurityDashboard from './components/Security-dashboard';
import EksMonitoringDashboard from './components/EksMonitoringDashboard';
import QAdashboard from './components/QAdashboard';
import UserConfigPage from './components/UserConfigPage'; // Import the new UserConfigPage
import DatabaseDashboard from './components/DatabaseDashboard'; // Placeholder for Database/Service Configuration
import DevelopmentDashboard from './components/DevelopmentDashboard'; // Placeholder for Development/CI-CD
import PlatformDeepDive from './components/PlatformDeepDive'; // Placeholder for Platform Deep Dive

import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import Security from './pages/Security';
import Status from './pages/Status';
import Docs from './pages/Docs';
import Contact from './pages/Contact';
import ManageCookies from './pages/ManageCookies';

// Get client ID from environment variable
const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Create a theme
const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
    success: {
      main: '#80D1FF',
      light: '#8FC8F0',
      dark: '#3E7AA3'
    },
    background: {
      default: '#F0F4F8',
      paper: '#ffffff',
    },
    text: {
      primary: '#2E2E2E',
      secondary: '#64748B',
    },
    border: {
      main: '#CFD8DC' // A light grey border for light mode
    }
  },
  typography: {
    fontFamily: '"Proxima Nova", sans-serif',
  },
});

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#90caf9',
    },
    secondary: {
      main: '#f48fb1',
    },
    success: {
      main: '#80D1FF',
      light: '#8FC8F0',
      dark: '#3E7AA3'
    },
    background: {
      default: '#00244E',
      paper: '#000000',
    },
    text: {
      primary: '#ffffff',
      secondary: '#B0B8C4',
    },
    border: {
      main: '#9BE0EA'
    }
  },
  typography: {
    fontFamily: '"Proxima Nova", sans-serif',
  },
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  // Initialize Supabase auth state listener
  useEffect(() => {
    // Check active sessions and set the user
    const getInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
      setIsAuthenticated(!!session?.user);
      setLoading(false);
    };
    
    getInitialSession();

    // Listen for changes in auth state
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setUser(session?.user ?? null);
        setIsAuthenticated(!!session?.user);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleThemeChange = () => {
    setDarkMode(!darkMode);
  };

  const checkAdminStatus = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        setIsAdmin(false);
        return;
      }

      const ADMIN_CHECK_URL = "https://fztfytovnyxxetzgjzxl.supabase.co/functions/v1/authentication/admin-check";

      const response = await axios.get(ADMIN_CHECK_URL, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setIsAdmin(response.data.isAdmin);
    } catch (error) {
      console.error('Error checking admin status in App.js:', error);
      setIsAdmin(false);
    }
  };

  useEffect(() => {
    // Check admin status when the app loads or when auth token might change
    // This depends on how login flow sets the token
    const token = localStorage.getItem('auth_token');
    if (token) {
      checkAdminStatus();
    }
    // Add listener for storage changes if login happens in a different tab/window
    // or re-check on specific events like navigation to protected routes.
  }, []); // Re-run if needed based on auth changes

  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <ThemeProvider theme={darkMode ? darkTheme : lightTheme}>
        <CssBaseline />
        <Router>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/create-ticket" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <CreateTicket />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/tickets/:ticketNumber?" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <TrackTickets isAdmin={isAdmin} />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                {/* Keep the old route for backward compatibility */}
                <Route path="/track-tickets" element={
                  <Navigate to="/tickets" replace />
                } />
                <Route path="/home" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <DevOpsDashboard isAdmin={isAdmin} />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/support-dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <SupportDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/monitoring-dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <MonitoringDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/aws-accounts" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <AwsAccounts />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                
                {/* Cloud Management Route */}
                <Route path="/cloud" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <CloudDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                
                {/* EKS Monitoring Route */}
                <Route path="/eks-monitoring/:accountId" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <EksMonitoringDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                
                {/* ✅ FIXED: AWS Cost Dashboard Route Now Accepts accountId */}
                <Route path="/aws-cost-dashboard/:accountId" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <AwsCostDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />

                <Route path="/github-dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <GitHubDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/users" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <Users />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/repositories" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <Repositories />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/issues" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <Issues />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/pull-requests" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <PullRequests />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/dialy-productivity" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <DailyProductivity />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <Profile />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/user-configuration" element={ // New route for User Configurations
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <UserConfigPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/qa-dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <QAdashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
                <Route path="/database" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <DatabaseDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />

                <Route path="/development" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <DevelopmentDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />

                <Route path="/platform-deep-dive" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <PlatformDeepDive />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />

                <Route path="/" element={<Navigate to="/home" />} />

                {/* Footer Routes - Accessible without authentication */}
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/security" element={<Security />} />
                <Route path="/status" element={<Status />} />
                <Route path="/docs" element={<Docs />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/manage-cookies" element={<ManageCookies />} />
                
                {/* Application Security Dashboard */}
                <Route path="/security-dashboard" element={
                  <ProtectedRoute>
                    <DashboardLayout isAdmin={isAdmin} darkMode={darkMode} handleThemeChange={handleThemeChange}>
                      <SecurityDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                } />
              </Routes>

        </Router>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
